// sharedPricingCalculations.js - Frontend pricing module used by all components
// This ensures consistent calculations across all creation/editing flows

/**
 * Configuration - load from environment or props
 */
const getConfig = (jobType = "retail") => {
  // For now, use constants. Later can load from props or API
  return {
    DEALER_PLATFORM_FEE_RATE: 0.05,      // 5% for dealers
    SPECIALIST_PLATFORM_FEE_RATE: 0.05,   // 5% for specialists
  };
};

/**
 * Service-level calculations
 */
export class SharedServicePricing {
  /**
   * Calculate final service amount after discount
   */
  static calculateServiceTotal(service) {
    const amount = parseFloat(service.service_cost || service.service_amount || 0);
    const discount = parseFloat(service.service_discount || 0);
    return Math.max(0, amount - discount);
  }

  /**
   * Validate service discount
   */
  static validateServiceDiscount(service, proposedDiscount) {
    const serviceCost = parseFloat(service.service_cost || service.service_amount || 0);
    const isValid = proposedDiscount >= 0 && proposedDiscount <= serviceCost;
    
    return {
      isValid,
      maxDiscount: serviceCost,
      validatedDiscount: isValid ? proposedDiscount : Math.min(proposedDiscount, serviceCost)
    };
  }

  /**
   * Calculate dealer margin for a service
   */
  static calculateServiceMargin(service) {
    if (!service.retail_price) return 0;
    const retailPrice = parseFloat(service.retail_price || 0);
    const finalAmount = this.calculateServiceTotal(service);
    return Math.max(0, retailPrice - finalAmount);
  }
}

/**
 * Request-level calculations - SAME LOGIC FOR ALL COMPONENTS
 */
export class SharedRequestPricing {
  /**
   * Calculate request total (sum of original service amounts, quantity-aware)
   */
  static calculateRequestTotal(services) {
    return services.reduce((sum, service) => {
      const unitPrice = parseFloat(service.unit_price || service.service_cost || service.service_amount || 0);
      const quantity = parseInt(service.quantity, 10) || 1;
      return sum + (unitPrice * quantity);
    }, 0);
  }

  /**
   * Calculate total discounts across all services
   */
  static calculateRequestDiscount(services) {
    return services.reduce((sum, service) => {
      return sum + parseFloat(service.service_discount || 0);
    }, 0);
  }

  /**
   * Calculate final amount after all discounts
   */
  static calculateRequestFinalAmount(services) {
    return services.reduce((sum, service) => {
      return sum + SharedServicePricing.calculateServiceTotal(service);
    }, 0);
  }

  /**
   * Calculate platform fee based on final amount (NEW: post-discount)
   */
  static calculatePlatformFee(services, jobType = "retail") {
    const finalAmount = this.calculateRequestFinalAmount(services);
    const config = getConfig(jobType);
    const feeRate = jobType === "dealer" 
      ? config.DEALER_PLATFORM_FEE_RATE 
      : config.SPECIALIST_PLATFORM_FEE_RATE;
    
    return finalAmount * feeRate;
  }

  /**
   * Calculate the taxable portion of the final amount (excludes non-taxable services)
   */
  static calculateTaxableAmount(services) {
    return services.reduce((sum, service) => {
      if (service.taxable === 0 || service.taxable === false) return sum;
      return sum + SharedServicePricing.calculateServiceTotal(service);
    }, 0);
  }

  /**
   * Calculate taxes on taxable services only (retail jobs only)
   */
  static calculateTaxes(services, taxRate, jobType = "retail") {

    if (jobType === "dealer") return 0; // Dealers don't pay taxes

    const taxableAmount = this.calculateTaxableAmount(services);
    return taxableAmount * (taxRate / 100);
  }

  /**
   * Calculate total duration (quantity-aware)
   */
  static calculateTotalDuration(services) {
    return services.reduce((sum, service) => {
      const duration = parseFloat(service.duration_days || 0);
      const quantity = parseInt(service.quantity, 10) || 1;
      return sum + (duration * quantity);
    }, 0);
  }

  /**
   * Get complete pricing breakdown - MASTER CALCULATION FUNCTION
   */
  static getCompletePricingBreakdown(services, options = {}) {
    const {
      jobType = "retail",
      taxRate = 0
    } = options;

    const breakdown = {
      // Service totals
      originalTotal: this.calculateRequestTotal(services),
      totalDiscount: this.calculateRequestDiscount(services),
      finalAmount: this.calculateRequestFinalAmount(services),
      
      // Fees and taxes (NEW: post-discount)
      platformFee: this.calculatePlatformFee(services, jobType),
      taxes: this.calculateTaxes(services, taxRate, jobType),
      
      // Duration
      totalDuration: this.calculateTotalDuration(services),
      
      // Metadata
      jobType,
      taxRate,
      serviceCount: services.length
    };

    // Add dealer-specific calculations if applicable
    if (jobType === "dealer") {
      const retailTotal = services.reduce((sum, service) => {
        return sum + parseFloat(service.retail_price || service.service_cost || 0);
      }, 0);
      
      breakdown.dealerTotals = {
        wholesaleTotal: breakdown.finalAmount,
        retailTotal: retailTotal,
        totalMargin: retailTotal - breakdown.finalAmount
      };
    }

    // Calculate final customer amount
    breakdown.customerTotal = breakdown.finalAmount + breakdown.taxes;

    return breakdown;
  }
}

/**
 * Utility functions for formatting and display
 */
export class SharedPricingUtils {
  /**
   * Format currency for display
   */
  static formatCurrency(amount) {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  }

  /**
   * Validate pricing data
   */
  static validatePricingData(services) {
    const errors = [];
    
    services.forEach((service, index) => {
      if (!service.service_id) {
        errors.push(`Service ${index}: Missing service_id`);
      }
      if (!service.service_cost && !service.service_amount && !service.unit_price) {
        errors.push(`Service ${index}: Missing service cost`);
      }
      if (service.service_discount < 0) {
        errors.push(`Service ${index}: Invalid discount (negative)`);
      }
      if (service.service_discount > (service.service_cost || service.service_amount || 0)) {
        errors.push(`Service ${index}: Discount exceeds service cost`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Prepare data for backend submission with all required database fields
   */
  static prepareForSubmission(services, options = {}) {
    const breakdown = SharedRequestPricing.getCompletePricingBreakdown(services, options);
    
    // Prepare services with all required fields for database insertion
    const preparedServices = services.map((service, index) => {
      const quantity = parseInt(service.quantity, 10) || 1;
      const unitPrice = parseFloat(service.unit_price || service.service_cost || service.service_amount || 0);
      const serviceCost = unitPrice * quantity;
      const serviceDiscount = parseFloat(service.service_discount || 0);
      const serviceTotal = serviceCost - serviceDiscount;
      
      // Calculate service-level fee on final amount
      const config = getConfig(options.jobType);
      const feeRate = options.jobType === "dealer" 
        ? config.DEALER_PLATFORM_FEE_RATE 
        : config.SPECIALIST_PLATFORM_FEE_RATE;
      const serviceFee = serviceTotal * feeRate;
      
      // Validate calculations - throw error if we get NaN
      if (isNaN(serviceCost)) {
        throw new Error(`Service ${index}: Invalid service cost - got NaN from service_cost: ${service.service_cost}, service_amount: ${service.service_amount}`);
      }
      if (isNaN(serviceDiscount)) {
        throw new Error(`Service ${index}: Invalid service discount - got NaN from service_discount: ${service.service_discount}`);
      }
      if (isNaN(serviceTotal)) {
        throw new Error(`Service ${index}: Invalid service total - calculation resulted in NaN (${serviceCost} - ${serviceDiscount})`);
      }
      if (isNaN(serviceFee)) {
        throw new Error(`Service ${index}: Invalid service fee - calculation resulted in NaN (${serviceTotal} * ${feeRate})`);
      }
      
      const preparedService = {
        ...service,
        // Database fields with validated calculations
        id: service.id || null,
        quantity: quantity,
        unit_price: unitPrice,
        service_amount: serviceCost,
        service_value: serviceTotal,  // Final amount after discount
        service_discount: serviceDiscount,
        service_fee: serviceFee,      // Service-level platform fee
        service_duration: parseFloat(service.duration_days || 0) * quantity,
        service_state: service.service_state || 'new',
        custom_description: service.description || service.custom_description || '',
        retail_price: service.retail_price ? parseFloat(service.retail_price) : null
      };
      
      return preparedService;
    });
    
    return {
      services: preparedServices,
      calculatedTotals: {
        Request_Total: breakdown.originalTotal,
        Request_Discount: breakdown.totalDiscount,
        Request_Fee: breakdown.platformFee,        // NEW: Post-discount fee
        Request_taxes: breakdown.taxes,
        request_duration: breakdown.totalDuration
      },
      breakdown: breakdown // For debugging/validation
    };
  }
}

// Legacy compatibility functions to replace existing serviceUtils
export const calculateRequestTotal = SharedRequestPricing.calculateRequestTotal;
export const calculateRequestDiscount = SharedRequestPricing.calculateRequestDiscount;
export const calculateFinalAmount = SharedServicePricing.calculateServiceTotal;